import { useEffect, useState } from "react";
import { ImagePlus, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";

import type { SiteContent } from "../../../api/types";
import { productImages, setProductImages, setProductPrice } from "../../../model/hostExtras";
import { CropUploadDialog } from "./CropUploadDialog";
import { ProxiedImage } from "./ProxiedImage";

/** 产品最多几张图(宿主产品详情页轮播)。 */
export const PRODUCT_IMAGES_MAX = 6;

function PriceInput({
  label,
  value,
  disabled,
  onCommit,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  onCommit: (v: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <Input
      aria-label={label}
      className="h-7 text-xs"
      placeholder="如「¥199 起」/「面议」"
      maxLength={30}
      value={text}
      disabled={disabled}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => text !== value && onCommit(text)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && text !== value) onCommit(text);
      }}
    />
  );
}

/**
 * 1009 编辑页「产品价格与多图」:每个产品的价格 / 原价(选填,原样展示)+ 多图(首张即产品卡图)。
 * ★ 价格是事实:只能你填(出处记「你填写的」)或来自资料采集,AI 不会编价格。同步到宿主产品时价格 / 原价 / 全部图片一起带上。
 */
export function HostProductsPanel({
  content,
  upload,
  onChange,
  disabled,
}: {
  content: SiteContent;
  /** 裸 base64 + 产品下标 → 完整 url。 */
  upload: (productIndex: number, base64: string) => Promise<string>;
  onChange: (update: (prev: SiteContent) => SiteContent) => void;
  disabled?: boolean;
}) {
  const [active, setActive] = useState<number | null>(null);
  const rows = content.products
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => typeof p?.name === "string" && p.name.trim() !== "");
  if (rows.length === 0) return null;

  return (
    <section
      aria-label="产品价格与多图"
      className={cn("space-y-3 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <div>
        <h2 className="text-sm font-semibold text-foreground">产品价格与多图</h2>
        <p className="mt-0.5 text-2xs text-muted-foreground">
          价格 / 原价选填,原样展示;只填你确认过的价格,AI 不会替你编。第一张图就是产品卡上的图。
        </p>
      </div>
      <ul className="space-y-3">
        {rows.map(({ p, i }) => {
          const name = String(p.name);
          const imgs = productImages(p);
          const price = typeof p.price === "string" ? p.price : "";
          const original = typeof p.originalPrice === "string" ? p.originalPrice : "";
          return (
            <li
              key={i}
              role="group"
              aria-label={`产品 · ${name}`}
              className="space-y-2 rounded-md border border-border/70 p-2"
            >
              <div className="text-xs font-medium text-foreground">{name}</div>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-1 text-2xs text-muted-foreground">
                  <span aria-hidden>价格</span>
                  <PriceInput
                    label={`价格:${name}`}
                    value={price}
                    disabled={disabled}
                    onCommit={(v) => onChange((prev) => setProductPrice(prev, i, "price", v))}
                  />
                </div>
                <div className="space-y-1 text-2xs text-muted-foreground">
                  <span aria-hidden>原价</span>
                  <PriceInput
                    label={`原价:${name}`}
                    value={original}
                    disabled={disabled}
                    onCommit={(v) =>
                      onChange((prev) => setProductPrice(prev, i, "originalPrice", v))
                    }
                  />
                </div>
              </div>
              <ul aria-label={`产品图:${name}`} className="flex flex-wrap gap-2">
                {imgs.map((u, j) => (
                  <li key={u} className="relative h-14 w-14 overflow-hidden rounded bg-muted">
                    <ProxiedImage
                      value={u}
                      alt={`${name} 图 ${j + 1}`}
                      className="h-full w-full object-cover"
                    />
                    {j === 0 && (
                      <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-center text-2xs text-white">
                        产品卡
                      </span>
                    )}
                    <button
                      type="button"
                      aria-label={`删除 ${name} 图 ${j + 1}`}
                      disabled={disabled}
                      className="absolute right-0 top-0 rounded-bl bg-black/50 p-0.5 text-white"
                      onClick={() =>
                        onChange((prev) =>
                          setProductImages(
                            prev,
                            i,
                            productImages(prev.products[i]).filter((x) => x !== u)
                          )
                        )
                      }
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
                {imgs.length < PRODUCT_IMAGES_MAX && (
                  <li>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-14 w-14 flex-col gap-0.5 p-0 text-2xs"
                      disabled={disabled}
                      aria-label={`添加产品图:${name}`}
                      onClick={() => setActive(i)}
                    >
                      <ImagePlus className="h-4 w-4" />
                      加图
                    </Button>
                  </li>
                )}
              </ul>
            </li>
          );
        })}
      </ul>
      <CropUploadDialog
        open={active !== null}
        onOpenChange={(o) => {
          if (!o) setActive(null);
        }}
        title="上传产品图"
        defaultAspect={null}
        upload={(b64) =>
          active !== null ? upload(active, b64) : Promise.reject(new Error("没有选中产品"))
        }
        onDone={(url) => {
          if (active !== null) {
            const idx = active;
            onChange((prev) =>
              setProductImages(prev, idx, [...productImages(prev.products[idx]), url])
            );
          }
        }}
      />
    </section>
  );
}
